import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  authRequest,
  authGoogleRequest,
  forgotRequest,
  passwordRequest,
  validationRequest,
  systemSigninRequest,
  signUpStartRequest,
  signUpCodeRequest,
  signUpGoogleRequest,
  signUpResendRequest,
  signUpWorkspaceRequest,
  newSigninRequest,
} from 'core/services/authentication';

import { IGroupPermissionResponse, ILearnerForumError, IPlayerResponse, ITokenAction } from 'types/action/player';
import { ICoursePermission } from 'types/domain/permissions';
import { LoadingState, SavingState } from 'types/domain/base';
import { Role, IRoleConfig } from 'types/domain/role';
import { IOrgUserVariable } from 'types/domain/user';
import { IKeyArray } from 'types/IKeyArray';
import { AuthState, IAuthResponse, IName, IResetAction, IValidateResponse, ValidateState } from 'types/session/auth';
import { PersistedState } from 'redux-persist';
import { ISession, ISessionAttemptDAO } from 'types/domain/session';
import { IOrganisationConfigDAO, OrganisationStatus } from 'types/domain/organisation';
import { hydrate, switchUser } from 'core/actions/product/state';
import { IError } from 'types/app/error';
import { IGroup } from 'types/domain/group';
import { IExistingWorkspace, IOnboarding } from 'types/domain/onboarding';
import { preview } from './register';
import {
  actionRequest,
  completeRequest,
  configRequest,
  dialogueRequest,
  groupPermissionRequest,
  learnerForumPostCreateRequest,
  learnerForumTopicCreateRequest,
  learnerForumTopicRequest,
  learnerForumTopicsRequest,
  searchRequest,
  tokenRequest,
} from 'core/services/product';

export interface ISessionReducer extends PersistedState {
  organisationId: any;
  name: IName;
  email: string;
  avatar: string;
  auth: Auth;
  validate: Validate;
  signup: ISignUp;
  role: Role;
  courseId: number;
  roleConfig: IRoleConfig;
  jwt: string;
  refresh: string;
  expires: string;
  tokens: IKeyArray<IToken>;
  inviteTokens: IKeyArray<string>;
  variables: IKeyArray<IOrgUserVariable>;
  attempts: IKeyArray<ISessionAttemptDAO>;
  groups: IKeyArray<IGroup>;
  mode: Mode;
  session: ISession;
  config: Partial<IOrganisationConfigDAO>;
  homeNodeId: number;
  passwordTokens: IKeyArray<IPasswordToken>;
  domainId: number;
  error: IError;
  organisationUserId: number;
  organisationStatus: OrganisationStatus;
  /** Set while a new workspace is still being onboarded (from the auth response). */
  onboarding: IOnboarding | null;
  deviceId: string;
  /**
   * True once the persisted session has been read back out of storage and
   * `hydrate` has run — regardless of whether anything was stored.
   *
   * Nothing may decide "logged out" before this flips: until it does, an
   * authenticated user is indistinguishable from an anonymous one, and a JWT
   * refresh may still be in flight. See `selectAuthPending`.
   */
  hydrated: boolean;
}

export interface ISignUp {
  state: SavingState;
  error: string;
  stage: SignUpStage;
  /** Encrypted sign-up state from the server, passed back on each step. */
  token: string;
  route: 'email' | 'google';
  email: string;
  firstName: string;
  lastName: string;
  avatar: string;
  company: string;
  /** Address suggested from the company name. */
  domain: string;
  existing: IExistingWorkspace | null;
}

/** 01 → 02 → 04 by email, Google → 03 → 04. */
export enum SignUpStage {
  ACCOUNT = 0,
  CONFIRM = 1,
  GOOGLE = 2,
  WORKSPACE = 3,
  COMPLETE = 4,
}

export interface Auth {
  state: AuthState;
  error: string;
}
export interface Validate {
  state: ValidateState;
  error: string;
}

export enum Mode {
  LEARNING = 0,
  PREVIEW = 1,
  ADMIN = 2,
}

export enum TokenErrorStatus {
  PRIVATE = 0,
  SCORM = 1,
  DRAFT = 2,
}

export interface IToken {
  token: string;
  entityToken?: string;
  inviteToken?: string;
  courseProgressId?: number;
  /** The course's completion from the last player response, 0–1. */
  courseProgressStats?: number;
  moduleId?: number;
  sessionAttemptId?: number;
  actionId?: number;
  loading?: LoadingState;
  saving?: SavingState;
  created?: number;
  index?: number;
  state?: TokenState;
  message?: string;
  scorm?: boolean;
  static?: boolean;
  renew?: boolean;
  restart?: boolean;
  status?: TokenErrorStatus;
  isStoodDown?: boolean;
  isVerificationRequired?: boolean;
  /** From the token response; later player responses do not repeat it. */
  coursePermission?: ICoursePermission | null;
  isReview?: boolean;
  /** A learner forum route answered 403 — the permission is gone for this session. */
  discussionDenied?: boolean;
}

/** A 403 from a learner forum route: hide the discussion for the rest of the session. */
function denyDiscussion(
  state: ISessionReducer,
  action: { payload?: ILearnerForumError; meta: { arg: { token?: string } } },
) {
  const { token } = action.meta.arg;
  if (action.payload?.status === 403 && token && state.tokens[token]) state.tokens[token].discussionDenied = true;
}

export enum TokenState {
  NEW = 0,
  PROGRESS = 1,
  CAN_RESTART = 2,
  SHOW_RESTART = 3,
}

export interface IPasswordToken {
  token: string;
  email?: string;
  response?: string;
  loading?: LoadingState;
  saving?: SavingState;
}

export const initialSignUp: ISignUp = {
  stage: SignUpStage.ACCOUNT,
  state: null,
  error: null,
  token: null,
  route: 'email',
  email: null,
  firstName: null,
  lastName: null,
  avatar: null,
  company: null,
  domain: null,
  existing: null,
};

const initialState: ISessionReducer = {
  name: null,
  auth: {
    state: AuthState.NOT_LOGGED_IN,
    error: null,
  },
  validate: {
    state: ValidateState.NOT_VALIDATED,
    error: null,
  },
  signup: initialSignUp,
  jwt: null,
  expires: null,
  refresh: null,
  role: null,
  roleConfig: null,
  courseId: null,
  tokens: {},
  inviteTokens: {},
  variables: {},
  attempts: {},
  groups: {},
  mode: Mode.LEARNING,
  _persist: null,
  session: null,
  config: null,
  homeNodeId: null,
  passwordTokens: {},
  domainId: null,
  error: null,
  email: null,
  avatar: null,
  organisationUserId: null,
  organisationStatus: null,
  onboarding: null,
  organisationId: undefined,
  deviceId: globalThis.crypto.randomUUID(),
  hydrated: false,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    /** Back to 01, keeping what was typed ("Use a different email", an expired token). */
    restartSignUp(state) {
      state.signup = { ...initialSignUp, email: state.signup.email, company: state.signup.company };
    },
    setSignUpStage(state, action: PayloadAction<SignUpStage>) {
      state.signup.stage = action.payload;
      state.signup.state = null;
      state.signup.error = null;
    },
    setSignUpCompany(state, action: PayloadAction<string>) {
      state.signup.company = action.payload;
    },
    setOnboarding(state, action: PayloadAction<IOnboarding | null>) {
      state.onboarding = action.payload;
    },
    setVariable(state, action: PayloadAction<IOrgUserVariable>) {
      state.variables[action.payload.id] = action.payload;
    },
    setGroup(state, action: PayloadAction<IGroup>) {
      state.groups[action.payload.id] = action.payload;
    },
    setAttempt(state, action: PayloadAction<ISessionAttemptDAO>) {
      state.attempts[action.payload.id] = action.payload;
    },
    removeGroup(state, action: PayloadAction<number>) {
      delete state.groups[action.payload];
    },
    updateMode(state, action: PayloadAction<Mode>) {
      state.mode = action.payload;
    },

    logout(state) {
      state.jwt = null;
      state.refresh = null;
    },
    restartToken(state, action: PayloadAction<IToken>) {
      state.tokens[action.payload.token].state = TokenState.NEW;
      state.tokens[action.payload.token].loading = LoadingState.LOADING;
    },
    updateToken(state, action: PayloadAction<IToken>) {
      const { token } = action.payload as IToken;

      state.tokens[token] = state.tokens[token]
        ? {
            ...state.tokens[token],
            ...action.payload,
          }
        : {
            loading: LoadingState.DEFAULT,
            ...action.payload,
            token,
            created: new Date().getTime(),
          };
    },
    updateTokenState(state, action: PayloadAction<IToken>) {
      const { token } = action.payload as IToken;
      state.tokens[token] = {
        ...state.tokens[token],
        ...action.payload,
      };
    },
    setError(state, action: PayloadAction<IError>) {
      state.error = action.payload;
    },
    clearError(state, action: PayloadAction<IError>) {
      state.error = null;
    },
    clearCache(state) {
      // `hydrated` survives the reset: storage has already been read, and
      // dropping it would put the boundary back on its loading gate forever.
      return { ...initialState, hydrated: state.hydrated };
    },
  },
  extraReducers: builder => {
    builder
      /**
       * A different organisation user now owns this device.
       *
       * The token entries are kept but scrubbed: `selectToken` and the sagas that
       * follow a sign-in still need the course link, while everything hanging off
       * it — the course progress, the resume position, the learner's permission —
       * described the previous user and is exactly what was leaking across.
       */
      .addCase(switchUser, (state, action) => {
        Object.keys(state.tokens).forEach(key => {
          const { token, entityToken, inviteToken, scorm, static: isStatic } = state.tokens[key] ?? {};
          state.tokens[key] = { token: token ?? key, entityToken, inviteToken, scorm, static: isStatic };
        });

        state.variables = {};
        state.attempts = {};
        state.groups = {};
        state.session = null;
        state.homeNodeId = null;
        state.error = null;
        state.name = null;
        state.email = null;
        state.avatar = null;
        state.organisationUserId = action.payload.organisationUserId;
      })
      .addCase(hydrate, (state, action) => {
        if (action.payload.session) {
          Object.keys(action.payload.session)
            .filter(type => !['search', 'config', 'passwordToken'].includes(type))
            .forEach(
              type =>
                (state[type] = action.payload.session[type]
                  ? typeof action.payload.session[type] === 'object'
                    ? { ...state[type], ...action.payload.session[type] }
                    : action.payload.session[type]
                  : state[type]),
            );
          state.validate.state = ValidateState.NOT_VALIDATED;
        }
        // Outside the `if`: an empty payload still means storage has been read,
        // it just had nothing in it.
        state.hydrated = true;
      })
      .addCase(validationRequest.pending, state => {
        state.validate.state = ValidateState.VALIDATING;
        state.validate.error = null;
      })
      .addCase(validationRequest.rejected, (state, action) => {
        state.validate.state = ValidateState.ERROR;
        state.validate.error = action.error.message;
        if (process.env.NODE_ENV !== 'development') state.jwt = null;
      })
      .addCase(validationRequest.fulfilled, (state, action) => {
        const { token, refresh, role, expires, organisationStatus, organisationUserId, courseId } =
          action.payload as IValidateResponse;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.jwt = token;
        state.refresh = refresh;
        state.role = role;
        state.expires = expires;
        state.courseId = courseId;
        state.organisationStatus = organisationStatus;
        state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
        state.organisationUserId = organisationUserId;
      })

      .addCase(authRequest.pending, state => {
        state.auth.state = AuthState.LOGGING_IN;
        state.auth.error = null;
      })
      .addCase(authRequest.rejected, (state, action) => {
        state.auth.state = AuthState.ERROR;
        state.auth.error = action.error.message;
      })
      .addCase(authRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          email,
          avatar,
          token,
          refresh,
          domainId,
          expires,
          organisationUserId,
          organisationStatus,
          courseId,
        } = action.payload as IAuthResponse;

        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.domainId = domainId;
        state.expires = expires;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
        state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
      })
      .addCase(authGoogleRequest.pending, state => {
        state.auth.state = AuthState.LOGGING_IN;
        state.auth.error = null;
      })
      .addCase(authGoogleRequest.rejected, (state, action) => {
        state.auth.state = AuthState.ERROR;
        state.auth.error = action.error.message;
      })
      .addCase(authGoogleRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          courseId,
          name,
          email,
          avatar,
          token,
          refresh,
          domainId,
          organisationUserId,
          organisationStatus,
        } = action.payload as IAuthResponse;

        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.domainId = domainId;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
        state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
      })
      .addCase(restartToken, (state, action) => {
        const { token } = action.payload;
        state.tokens[token] = {};
      })
      .addCase(tokenRequest.pending, (state, action) => {
        const { token } = action.meta.arg as ITokenAction;
        state.tokens[token] = {
          ...state.tokens[token],
          loading: LoadingState.LOADING,
        };
      })
      .addCase(tokenRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as ITokenAction;
        state.tokens[token] = { loading: LoadingState.ERROR, error: action.error };
      })
      .addCase(tokenRequest.fulfilled, (state, action) => {
        const { token, inviteToken } = action.meta.arg as ITokenAction;
        const { session, playerResponse, message, status } = action.payload;
        const activeToken: string = token || playerResponse?.token;

        if (inviteToken) state.inviteTokens[inviteToken] = activeToken;

        state.tokens[activeToken] = {
          ...(state.tokens[activeToken] ? { ...state.tokens[activeToken] } : {}),
          courseProgressId: playerResponse?.courseProgressId,
          courseProgressStats: playerResponse?.courseProgressStats ?? state.tokens[activeToken]?.courseProgressStats,
          isStoodDown: playerResponse?.isStoodDown,
          isVerificationRequired: playerResponse?.isVerificationRequired,
          loading: playerResponse ? LoadingState.LOADED : LoadingState.ERROR,
          static: playerResponse?.static,
          // Set here and on system sign-in only: no later player response carries
          // them, so anything else writing them would wipe the discussion.
          coursePermission: playerResponse?.coursePermission ?? null,
          isReview: !!playerResponse?.isReview,
          discussionDenied: false,
          status,
          message,
        };

        if (session && (!state.session || state.session.id !== session.id)) state.session = session;

        mergeRegister(state, playerResponse);
      })
      .addCase(systemSigninRequest.fulfilled, (state, action) => {
        const { token } = action.meta.arg as ITokenAction;
        //@ts-ignore
        const { session, playerResponse, message } = action.payload;

        state.tokens[token] = {
          ...(state.tokens[token] ? { ...state.tokens[token] } : {}),
          courseProgressId: playerResponse?.courseProgressId,
          isStoodDown: playerResponse?.isStoodDown,
          isVerificationRequired: playerResponse?.isVerificationRequired,
          coursePermission: playerResponse?.coursePermission ?? null,
          isReview: !!playerResponse?.isReview,
          discussionDenied: false,
          loading: playerResponse ? LoadingState.LOADED : LoadingState.ERROR,
          message,
        };

        if (session && (!state.session || state.session.id !== session.id)) state.session = session;
      })
      .addCase(learnerForumTopicsRequest.rejected, denyDiscussion)
      .addCase(learnerForumTopicRequest.rejected, denyDiscussion)
      .addCase(learnerForumTopicCreateRequest.rejected, denyDiscussion)
      .addCase(learnerForumPostCreateRequest.rejected, denyDiscussion)
      .addCase(actionRequest.fulfilled, (state, action) => {
        const playerResponse = action.payload as IPlayerResponse;

        mergeRegister(state, playerResponse);
      })
      .addCase(completeRequest.fulfilled, (state, action) => {
        const playerResponse = action.payload as IPlayerResponse;

        mergeRegister(state, playerResponse);
      })
      .addCase(dialogueRequest.fulfilled, (state, action) => {
        const playerResponse = action.payload as IPlayerResponse;
        if (playerResponse.auth) {
          state.jwt = playerResponse.auth.token;
          state.refresh = playerResponse.auth.refresh;
        }
        mergeRegister(state, playerResponse);
      })
      .addCase(groupPermissionRequest.fulfilled, (state, action) => {
        const { permission } = action.payload as IGroupPermissionResponse;
        state.tokens[permission.token] = { id: permission.id };
      })
      .addCase(configRequest.pending, (state, action) => {
        state.config = { loading: LoadingState.LOADING };
      })
      .addCase(configRequest.fulfilled, (state, action) => {
        state.config = { ...action.payload, loading: LoadingState.LOADED };
      })
      .addCase(configRequest.rejected, (state, action) => {
        state.config = { loading: LoadingState.ERROR };
      })

      .addCase(forgotRequest.pending, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = { ...state.passwordTokens[token], saving: SavingState.SAVING };
      })
      .addCase(forgotRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.ERROR,
          response: action.error.message,
        };
      })
      .addCase(forgotRequest.fulfilled, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.SAVED,
          response: action.payload,
        };
      })
      .addCase(passwordRequest.pending, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = { ...state.passwordTokens[token], saving: SavingState.SAVING };
      })
      .addCase(passwordRequest.rejected, (state, action) => {
        const { token } = action.meta.arg as IResetAction;
        state.passwordTokens[token] = {
          ...state.passwordTokens[token],
          saving: SavingState.ERROR,
          response: action.error.message,
        };
      })
      .addCase(passwordRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          token,
          courseId,
          refresh,
          email,
          avatar,
          organisationUserId,
          organisationStatus,
        } = action.payload as IAuthResponse;
        state.passwordTokens[action.meta.arg.token] = {
          ...state.passwordTokens[action.meta.arg.token],
          saving: SavingState.SAVED,
          response: action.payload,
        };
        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
        state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
      })
      .addCase(searchRequest.fulfilled, (state, action) => {
        if (action.payload.session && !state.session) state.session = action.payload.session;

        if (state.auth.state != AuthState.LOGGED_IN) {
          const {
            role,
            roleConfig,
            name,
            token,
            courseId,
            refresh,
            email,
            avatar,
            organisationUserId,
            organisationStatus,
          } = action.payload as IAuthResponse;
          state.auth.state = AuthState.LOGGED_IN;
          state.auth.error = null;
          state.validate.state = ValidateState.VALIDATED;
          state.validate.error = null;
          state.role = role;
          state.roleConfig = roleConfig;
          state.name = name;
          state.jwt = token;
          state.refresh = refresh;
          state.email = email;
          state.avatar = avatar;
          state.courseId = courseId;
          state.organisationUserId = organisationUserId;
          state.organisationStatus = organisationStatus;
          state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
        }
      })
      .addCase(signUpStartRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
        state.signup.error = null;
      })
      .addCase(signUpStartRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'We couldn’t create your account. Check your details and try again.';
      })
      .addCase(signUpStartRequest.fulfilled, (state, action) => {
        const { token, email, firstName, lastName, company } = action.payload;
        state.signup = {
          ...state.signup,
          state: null,
          error: null,
          stage: SignUpStage.CONFIRM,
          route: 'email',
          token,
          email,
          firstName,
          lastName,
          company,
        };
      })
      .addCase(signUpCodeRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
        state.signup.error = null;
      })
      .addCase(signUpCodeRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'That code is wrong or has expired.';
      })
      .addCase(signUpCodeRequest.fulfilled, (state, action) => {
        state.signup.state = null;
        state.signup.stage = SignUpStage.WORKSPACE;
        state.signup.token = action.payload.token;
        state.signup.domain = action.payload.domain ?? null;
      })
      .addCase(signUpResendRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'We couldn’t send a new code. Start again with your email.';
      })
      .addCase(signUpResendRequest.fulfilled, (state, action) => {
        state.signup.state = null;
        state.signup.error = null;
        state.signup.token = action.payload.token;
      })
      .addCase(signUpGoogleRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
        state.signup.error = null;
      })
      .addCase(signUpGoogleRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'Google sign-up didn’t work. Try again, or use your email.';
      })
      .addCase(signUpGoogleRequest.fulfilled, (state, action) => {
        const { token, email, firstName, lastName, avatar, existing } = action.payload;
        state.signup = {
          ...state.signup,
          state: null,
          error: null,
          stage: SignUpStage.GOOGLE,
          route: 'google',
          token,
          email,
          firstName,
          lastName,
          avatar: avatar ?? null,
          existing: existing ?? null,
        };
      })
      .addCase(signUpWorkspaceRequest.pending, state => {
        state.signup.state = SavingState.SAVING;
        state.signup.error = null;
      })
      .addCase(signUpWorkspaceRequest.rejected, state => {
        state.signup.state = SavingState.ERROR;
        state.signup.error = 'We couldn’t create your workspace. Try again.';
      })
      .addCase(signUpWorkspaceRequest.fulfilled, (state, action) => {
        const { success, errorMessage } = action.payload;
        if (success) {
          state.signup.state = SavingState.SAVED;
          state.signup.stage = SignUpStage.COMPLETE;
          return;
        }
        state.signup.state = SavingState.ERROR;
        state.signup.error = errorMessage;
        // an expired token can only be fixed by starting again
        if (errorMessage === 'expired') state.signup.stage = SignUpStage.ACCOUNT;
      })
      .addCase(newSigninRequest.fulfilled, (state, action) => {
        const {
          role,
          roleConfig,
          name,
          courseId,
          token,
          refresh,
          email,
          avatar,
          organisationStatus,
          organisationUserId,
        } = action.payload as IAuthResponse;
        state.auth.state = AuthState.LOGGED_IN;
        state.auth.error = null;
        state.validate.state = ValidateState.VALIDATED;
        state.validate.error = null;
        state.role = role;
        state.roleConfig = roleConfig;
        state.name = name;
        state.jwt = token;
        state.courseId = courseId;
        state.refresh = refresh;
        state.email = email;
        state.avatar = avatar;
        state.organisationUserId = organisationUserId;
        state.organisationStatus = organisationStatus;
        state.onboarding = (action.payload as { onboarding?: IOnboarding | null }).onboarding ?? null;
      })
      .addCase(preview, (state, action) => {
        state.mode = action.payload.mode ?? Mode.PREVIEW;
      });
  },
});

function mergeRegister(state: ISessionReducer, playerResponse: IPlayerResponse) {
  if (!playerResponse) return;

  const {
    attemptRegister,
    variableRegister,
    homeCourseNodeId,
    token,
    courseProgressId,
    courseProgressStats,
    isStoodDown,
    isVerificationRequired,
  } = playerResponse;

  attemptRegister.forEach(attempt => (state.attempts[attempt.moduleId] = attempt));
  variableRegister.forEach(orgUserVariable => (state.variables[orgUserVariable.variableId] = orgUserVariable));
  state.homeNodeId = homeCourseNodeId;

  if (token) {
    state.tokens[token] = {
      ...(state.tokens[token] ? { ...state.tokens[token] } : { token }),
      isStoodDown,
      isVerificationRequired,
    };
  }

  // Action and complete responses don't always echo the token, so the stats land
  // on whichever token holds this course progress. Null means the server didn't
  // load the row this request — keep the last value rather than dropping to 0.
  if (courseProgressStats != null) {
    Object.entries(state.tokens).forEach(([key, t]) => {
      if (t && (key === token || (courseProgressId && t.courseProgressId === courseProgressId)))
        t.courseProgressStats = courseProgressStats;
    });
  }
}

export const {
  setVariable,
  setGroup,
  setAttempt,
  removeGroup,
  updateMode,
  logout,
  updateToken,
  updateTokenState,
  restartToken,
  clearCache,
  setError,
  clearError,
  restartSignUp,
  setSignUpStage,
  setSignUpCompany,
  setOnboarding,
} = sessionSlice.actions;
export default sessionSlice.reducer;
